import { Component, h, Element, Method, Host, Prop } from '@stencil/core';
import { adjustContainerAndSlideWidths, goToNextSlide, goToPrevSlide, goToSlide, initSlides, setupEvents } from './stage-utils';

@Component({
  tag: 'dc-ui-web-stage',
  styleUrl: 'dc-ui-web-stage.scss',
  shadow: true,
})
export class DcUiWebStage {
  @Element() el: HTMLElement;
  @Prop() autoSlide = false;
  @Prop() autoSlideInterval = 5000;
  @Prop() pauseOnHover = true;
  @Prop() debug = false;

  private debugLog(message: string) {
    if (this.debug) {
      console.log(`[DcUiWebStage] ${message}`);
    }
  }

  /**
   * Navigates to the specified slide.
   *
   * @method
   * @async
   * @param {number} slideIndex - The index of the slide to navigate to.
   */
  @Method()
  async goToSlide(slideIndex: number) {
    this.debugLog(`Going to slide ${slideIndex}`);
    goToSlide(this, slideIndex);
  }

  stageWrapper: HTMLDivElement;
  slidesWrapper: HTMLDivElement;
  navigationDotList: HTMLElement;
  slides: NodeList;
  numSlides: number;
  activeSlideIndex = 0;
  swipeStartX: number;
  swipeEndX: number;
  stageWidth: number;
  hasRendered = false;

  connectedCallback() {
    this.slides = this.el.querySelectorAll('dc-ui-web-stage-item');
    this.numSlides = this.slides.length;
    this.debugLog(`Connected: ${this.numSlides} slides found`);
  }

  componentDidRender() {
    if (!this.hasRendered) {
      this.debugLog('First render, initializing slides');
      initSlides(this);

      if (this.numSlides > 1) {
        this.debugLog('Setting up events');
        setupEvents(this);
      }

      window.addEventListener('resize', () => {
        adjustContainerAndSlideWidths(this);
      });
    }
    this.hasRendered = true;
  }

  onLeftButtonClick = () => {
    this.debugLog('Left button clicked');
    goToPrevSlide(this);
  };

  onRightButtonClick = () => {
    this.debugLog('Right button clicked');
    goToNextSlide(this);
  };

  onDotClick = (e: Event) => {
    const dot = (e.target as HTMLElement).closest('button.dot');
    if (!dot) {
      return;
    }
    const slideIndex = Number(dot.getAttribute('data-slide-index'));
    this.debugLog(`Navigation dot clicked for slide ${slideIndex}`);
    goToSlide(this, slideIndex);
  };

  onStageMouseLeave = () => {
    this.debugLog('Mouse left stage, clearing focus');
    const buttons = this.stageWrapper.querySelectorAll('dc-ui-button');
    buttons.forEach(button => button.blur());
  };

  render() {
    return (
      <Host>
        <div class="stage-wrapper" ref={el => (this.stageWrapper = el)} onMouseLeave={this.onStageMouseLeave}>
          {this.numSlides > 1 && (
            <div class="slider-button-container slider-button-container--left">
              <dc-ui-button class="slider-button" variant="secondary" icon="dc_backward_left" hideLabel={true} onClick={this.onLeftButtonClick}>
                Previous
              </dc-ui-button>
            </div>
          )}
          <div class="slides-wrapper" ref={el => (this.slidesWrapper = el)}>
            <slot></slot>
          </div>
          {this.numSlides > 1 && (
            <div class="slider-button-container slider-button-container--right">
              <dc-ui-button class="slider-button" variant="secondary" icon="dc_forward_right" hideLabel={true} onClick={this.onRightButtonClick}>
                Next
              </dc-ui-button>
            </div>
          )}
        </div>
        {this.numSlides > 1 && <ul class="navigation-dots" onClick={this.onDotClick} ref={el => (this.navigationDotList = el)}></ul>}
      </Host>
    );
  }
}
